import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, TextField, MenuItem,
  Alert, CircularProgress, Divider, Grid,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { staffService } from '@smartgrow/shared';

type Role = 'ADMIN' | 'SUPERVISOR';

interface StaffForm {
  username: string;
  password: string;
  fullName: string;
  email: string;
  phone: string;
  role: Role;
  notes: string;
}

const initial: StaffForm = {
  username: '',
  password: '',
  fullName: '',
  email: '',
  phone: '',
  role: 'SUPERVISOR',
  notes: '',
};

export const NewStaffPage = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<StaffForm>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof StaffForm, string>>>({});

  const mutation = useMutation({
    mutationFn: (data: StaffForm) => staffService.createStaff(data as any),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff'] });
      navigate('/dashboard/staff');
    },
  });

  const validate = () => {
    const e: typeof errors = {};
    if (!form.username.trim() || form.username.length < 3) e.username = 'Username must be at least 3 characters';
    if (!form.password || form.password.length < 6) e.password = 'Password must be at least 6 characters';
    if (!form.fullName.trim()) e.fullName = 'Full name is required';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Valid email is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const set = (field: keyof StaffForm, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="text">Back</Button>
        <Typography variant="h4" fontWeight="bold">Add Staff Member</Typography>
      </Box>

      <Paper sx={{ p: 3, maxWidth: 680 }}>
        {mutation.isError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to create staff member. Username or email may already exist.
          </Alert>
        )}

        <Typography variant="h6" sx={{ mb: 2 }}>Account Details</Typography>

        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Full Name"
              value={form.fullName}
              onChange={(e) => set('fullName', e.target.value)}
              fullWidth error={!!errors.fullName} helperText={errors.fullName}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Username"
              value={form.username}
              onChange={(e) => set('username', e.target.value.toLowerCase().replace(/\s/g, ''))}
              fullWidth error={!!errors.username} helperText={errors.username}
              inputProps={{ maxLength: 50 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              fullWidth error={!!errors.email} helperText={errors.email}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Phone (optional)"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              fullWidth
              inputProps={{ maxLength: 20 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Password"
              type="password"
              value={form.password}
              onChange={(e) => set('password', e.target.value)}
              fullWidth error={!!errors.password} helperText={errors.password}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Role"
              value={form.role}
              onChange={(e) => set('role', e.target.value)}
              fullWidth
            >
              <MenuItem value="ADMIN">Admin</MenuItem>
              <MenuItem value="SUPERVISOR">Supervisor</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Notes (optional)"
              multiline rows={2}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              fullWidth inputProps={{ maxLength: 500 }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={() => navigate('/dashboard/staff')} disabled={mutation.isPending}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => validate() && mutation.mutate(form)}
            disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} /> : null}
          >
            Create Staff Member
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
